import React from 'react'
import { useAppDispatch } from '../../hooks/redux'

import { getAccounts, getEnvironment } from '../../lib/api'
import { AddErrorAlert } from '../../slices/alert'
import { useAuth } from '../AuthProvider'
import { useLoginState } from '../LoginStateProvider'

const AccountContext = React.createContext(null)

export const ACCOUNT_KEY = 'last_account'
export const getStoredAccount = () => localStorage.getItem(ACCOUNT_KEY)
export const setStoredAccount = val => localStorage.setItem(ACCOUNT_KEY, val)

const ACCOUNT_RESTRICTED_ERROR = 'eAuthInvalidClient'

const AccountProvider = ({ children }) => {
  const dispatch = useAppDispatch()
  const { isLoggedIn } = useAuth()
  const { redirectToAccounts } = useLoginState()

  const [accounts, setAccounts] = React.useState([])
  const [loaded, setLoaded] = React.useState(false)

  React.useEffect(() => {
    if (loaded || !isLoggedIn) {
      return
    }

    let mounted = true

    const handleGet = async () => {
      try {
        const accounts = await getAccounts()
        if (accounts.length === 0) {
          window.location.href = '/logout/'
          return
        }

        mounted && setAccounts(accounts)
      } catch (err) {
        console.error('Fetch error in AccountProvider', err)
        dispatch(AddErrorAlert({ err }))
      }
    }

    handleGet()

    return () => {
      mounted = false
    }
  }, [loaded, isLoggedIn, dispatch])

  // Save / verify current account
  React.useEffect(() => {
    if (accounts.length > 0) {
      const stored = getStoredAccount()
      let currentAccount = accounts[0].id

      if (stored) {
        // Check if allowed
        if (accounts.map(obj => obj.id).includes(stored)) {
          currentAccount = stored
        }
      }

      setStoredAccount(currentAccount)
      setLoaded(true)
    }
  }, [accounts])

  const callEnvironment = React.useCallback(
    async (account, redirect = true, cb) => {
      try {
        const data = await getEnvironment(account)

        // format response obj to match previous session_user
        const formatUser = {
          ...data.user,
          settings: data.settings,
          config: data.config
        }
        localStorage.setItem('session_user', JSON.stringify(formatUser))

        // redirects user back to their saved homepage
        if (redirect) {
          window.location.href = '/'
        }

        cb && cb(data)
      } catch (err) {
        console.error('Error getting environment', err)

        let { response, axiosError } = err
        if (!response) {
          response = axiosError?.response
        }

        if (response?.status === 403) {
          const code = response?.data?.error_code

          if (code === ACCOUNT_RESTRICTED_ERROR) {
            // Redirect to account screen
            redirectToAccounts(
              'Your access to this environment has been restricted.'
            )
            return
          }
        }

        dispatch(AddErrorAlert({ err }))
      }
    },
    [redirectToAccounts, dispatch]
  )

  return (
    <AccountContext.Provider value={{ accounts, loaded, callEnvironment }}>
      {children}
    </AccountContext.Provider>
  )
}

export default AccountProvider

export const useAccounts = () => React.useContext(AccountContext)
